import { useState, useEffect, useRef } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { DIETARY_OPTIONS, VEGAN_STYLE_OPTIONS, LEVEL_OPTIONS, GOAL_OPTIONS } from "../data";
import CityPicker from "./CityPicker";
import AllergiesSection from "./AllergiesSection";
import AllergySheetReview from "./AllergySheetReview";
import { normalizeToE164 } from "../lib/phoneHash";
import { supabase } from "../lib/supabase";

function ProgressDots({ total, current }) {
  return (
    <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? 20 : 6, height:6, borderRadius:3,
          background: i < current ? "#a8d5a2" : i === current ? COLOR.gold : COLOR.border,
          transition:"all 0.35s ease"
        }} />
      ))}
    </div>
  );
}

function OptionCard({ option, selected, onSelect, wide }) {
  return (
    <button onClick={() => onSelect(option.id)} style={{
      width:"100%", textAlign:"left",
      border:`1.5px solid ${selected ? COLOR.gold : COLOR.border}`,
      borderRadius:14, padding: wide ? "14px 16px" : "16px",
      background: selected ? COLOR.goldDeep : COLOR.soil,
      cursor:"pointer", display:"flex", alignItems:"center", gap:14,
      boxShadow: selected ? "0 0 0 1px #f5c84244, 0 4px 24px #f5c84211" : "none",
      transition:"all 0.2s", transform: selected ? "scale(1.01)" : "scale(1)"
    }}>
      <span style={{ fontSize: wide ? 24 : 28, flexShrink:0 }}>{option.emoji}</span>
      <div>
        <div style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize: wide ? 15 : 16,
          color: selected ? COLOR.gold : COLOR.ink, fontWeight:500, lineHeight:1.2 }}>
          {option.label}
        </div>
        <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.muted, marginTop:2 }}>
          {option.desc}
        </div>
      </div>
      {selected && (
        <div style={{ marginLeft:"auto", width:20, height:20, borderRadius:"50%",
          background:COLOR.gold, display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:11, color:COLOR.night, fontWeight:900, flexShrink:0 }}>✓</div>
      )}
    </button>
  );
}

function SplashScreen({ onNext }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", minHeight:"100vh", padding:32, textAlign:"center" }}>
      <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition:"all 0.7s ease" }}>
        <img
          src="/boodalogo.png"
          alt="Booda"
          style={{ width: 220, height: "auto", marginBottom: 16, display: "block", marginLeft: "auto", marginRight: "auto" }}
        />
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:16, color:COLOR.dim,
          lineHeight:1.6, maxWidth:260, margin:"0 auto 40px" }}>
          Learn to cook like a pro. One skill at a time.
        </p>
        <button onClick={onNext} style={{
          background:COLOR.gold, color:COLOR.night, border:"none", borderRadius:50,
          padding:"16px 40px", fontFamily:"'DM Mono', monospace", fontSize:13,
          fontWeight:600, letterSpacing:"0.08em", cursor:"pointer",
          boxShadow:"0 0 40px #f5c84244"
        }}>LET'S GET STARTED</button>
      </div>
    </div>
  );
}

function QuestionScreen({ stepLabel, title, subtitle, options, value, onSelect, onNext, onBack, step, total, nextLabel, wide }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", padding:"24px 20px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:COLOR.sub, fontSize:20, cursor:"pointer" }}>←</button>
        <ProgressDots total={total} current={step} />
        <div style={{ width:28 }} />
      </div>
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:10 }}>
          {stepLabel || `STEP ${step + 1} OF ${total}`}
        </div>
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:34, fontWeight:300, fontStyle:"italic",
          color:COLOR.ink, letterSpacing:"-0.02em", lineHeight:1.1 }}>{title}</h2>
        {subtitle && <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.muted, marginTop:8 }}>{subtitle}</p>}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap: wide ? 10 : 14, flex:1 }}>
        {options.map(opt => (
          <OptionCard key={opt.id} option={opt} selected={value === opt.id} onSelect={onSelect} wide={wide} />
        ))}
      </div>
      <button onClick={onNext} disabled={!value} style={{
        marginTop:24, width:"100%", padding:"16px",
        background: value ? COLOR.gold : COLOR.soil,
        color: value ? COLOR.night : COLOR.sub, border:"none", borderRadius:14,
        fontFamily:"'DM Mono', monospace", fontSize:13, fontWeight:600,
        letterSpacing:"0.08em", cursor: value ? "pointer" : "not-allowed", transition:"all 0.3s"
      }}>{nextLabel || "CONTINUE →"}</button>
    </div>
  );
}

function VeganStyleScreen({ value, onSelect, onNext, onBack, step, total }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", padding:"24px 20px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:COLOR.sub, fontSize:20, cursor:"pointer" }}>←</button>
        <ProgressDots total={total} current={step} />
        <div style={{ width:28 }} />
      </div>
      <div style={{ marginBottom:32 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:"#a8d5a2", letterSpacing:"0.15em", marginBottom:10 }}>
          STEP {step + 1} OF {total}
        </div>
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:34, fontWeight:300, fontStyle:"italic",
          color:COLOR.ink, letterSpacing:"-0.02em", lineHeight:1.1 }}>What's your vegan style?</h2>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.muted, marginTop:8 }}>
          This helps us show you the right version of every dish.
        </p>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:14, flex:1 }}>
        {VEGAN_STYLE_OPTIONS.map(opt => (
          <OptionCard key={opt.id} option={opt} selected={value === opt.id} onSelect={onSelect} />
        ))}
      </div>
      <div style={{ margin:"24px 0", padding:"14px 16px", background:COLOR.leafDeep,
        border:`1px solid ${COLOR.leafDim}`, borderRadius:12 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.leafMuted, letterSpacing:"0.1em", marginBottom:6 }}>FOR EXAMPLE</div>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:13, color:COLOR.leafMuted, lineHeight:1.5 }}>
          {value === "alternatives" && "Thanksgiving? We'll show you tofurkey, seitan roast, and stuffed squash."}
          {value === "whole"        && "Thanksgiving? Lentil loaf, mushroom wellington, roasted root vegetables."}
          {value === "flexible"     && "We'll show you both and let you choose based on the dish."}
          {!value                   && "Select an option to see how we'll personalize your recipes."}
        </p>
      </div>
      <button onClick={onNext} disabled={!value} style={{
        width:"100%", padding:"16px",
        background: value ? COLOR.gold : COLOR.soil,
        color: value ? COLOR.night : COLOR.sub, border:"none", borderRadius:14,
        fontFamily:"'DM Mono', monospace", fontSize:13, fontWeight:600,
        letterSpacing:"0.08em", cursor: value ? "pointer" : "not-allowed", transition:"all 0.3s"
      }}>CONTINUE →</button>
    </div>
  );
}

function BuildingScreen({ onDone, profile }) {
  const [progress, setProgress] = useState(0);
  const messages = ["Analyzing your profile...","Building your skill tree...","Adding seasonal events...","Personalizing recipes...","Almost ready..."];
  const [msg, setMsg] = useState(messages[0]);
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i++;
      setProgress(Math.min(i * 22, 100));
      setMsg(messages[Math.min(i, messages.length - 1)]);
      if (i >= 5) { clearInterval(id); setTimeout(onDone, 600); }
    }, 500);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", padding:40, textAlign:"center" }}>
      <img
        src="/boodalogo.png"
        alt=""
        aria-hidden
        style={{ width:140, height:"auto", marginBottom:24, animation:"obPulse 1.6s ease-in-out infinite" }}
      />
      <style>{`@keyframes obPulse { 0%,100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.05); opacity: 1; } }`}</style>
      <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:28, fontWeight:300, fontStyle:"italic", color:COLOR.ink, marginBottom:8 }}>
        Building your kitchen
      </h2>
      <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.muted, marginBottom:40 }}>{msg}</p>
      <div style={{ width:"100%", maxWidth:280, height:4, background:COLOR.edge, borderRadius:2, overflow:"hidden" }}>
        <div style={{ height:"100%", background:COLOR.gold, borderRadius:2, width:`${progress}%`,
          transition:"width 0.4s ease", boxShadow:"0 0 12px #f5c84266" }} />
      </div>
      <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

function CityScreen({ value, onChange, onNext, onBack, step, total }) {
  const canSkip = (value || []).length === 0;
  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", padding:"24px 20px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:COLOR.sub, fontSize:20, cursor:"pointer" }}>←</button>
        <ProgressDots total={total} current={step} />
        <div style={{ width:28 }} />
      </div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:10 }}>
          STEP {step + 1} OF {total}
        </div>
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:34, fontWeight:300, fontStyle:"italic",
          color:COLOR.ink, letterSpacing:"-0.02em", lineHeight:1.1 }}>Where do you shop?</h2>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.muted, marginTop:8 }}>
          Pick up to 3 nearby cities so we can surface local brands and house-brands from the grocers near you. Skip if you'd rather set this later.
        </p>
      </div>
      <div style={{ flex:1 }}>
        <CityPicker
          value={value || []}
          onChange={onChange}
          max={3}
          showGeolocate={true}
          compact={true}
        />
      </div>
      <button onClick={onNext} style={{
        marginTop:24, width:"100%", padding:"16px",
        background: COLOR.gold,
        color: COLOR.night, border:"none", borderRadius:14,
        fontFamily:"'DM Mono', monospace", fontSize:13, fontWeight:600,
        letterSpacing:"0.08em", cursor: "pointer", transition:"all 0.3s"
      }}>{canSkip ? "SKIP FOR NOW →" : "CONTINUE →"}</button>
    </div>
  );
}

// PhoneScreen — collect phone number during onboarding so the
// find-friends directory and step-assignment notifications work
// without a second trip to Settings. NO SMS verification — we trust
// the user to type their own number. Same pepper-hash flow as
// Settings → LET FRIENDS FIND ME. Skippable.
function PhoneScreen({ value, onChange, onNext, onBack, step, total }) {
  const normalized = normalizeToE164(value);
  const canSkip = !value || !value.trim();
  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", padding:"24px 20px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:COLOR.sub, fontSize:20, cursor:"pointer" }}>←</button>
        <ProgressDots total={total} current={step} />
        <div style={{ width:28 }} />
      </div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:10 }}>
          STEP {step + 1} OF {total}
        </div>
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:34, fontWeight:300, fontStyle:"italic",
          color:COLOR.ink, letterSpacing:"-0.02em", lineHeight:1.1 }}>What's your phone?</h2>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.ink, marginTop:14, lineHeight:1.55 }}>
          Two things this unlocks:
        </p>
        <ul style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.ink, marginTop:6, paddingLeft:18, lineHeight:1.55 }}>
          <li><strong>Auto-add family + friends.</strong> Anyone with you in their contacts who's already on Booda will surface for one-tap "+ FAMILY" — no code-swapping or invite links.</li>
          <li style={{ marginTop:6 }}><strong>Cook together notifications.</strong> When someone schedules a meal with you as a helper or diner, your phone lights up — same way it would for an iMessage.</li>
        </ul>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.muted, marginTop:14, lineHeight:1.5 }}>
          Your number stays on your device. We only send a one-way hash (think fingerprint) to match against the directory — the raw digits never reach our servers.
        </p>
      </div>
      <div style={{ flex:1 }}>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="+1 415 555 1212"
          style={{
            width:"100%", padding:"16px 18px", borderRadius:14,
            border:`1.5px solid ${normalized ? COLOR.gold : COLOR.border}`,
            background:COLOR.soil, color:COLOR.ink,
            fontFamily:"'DM Mono', monospace", fontSize:18,
            outline:"none", textAlign:"center", letterSpacing:"0.04em",
          }}
        />
        {value.trim() && !normalized && (
          <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.rose, marginTop:10, textAlign:"center" }}>
            Add the country code (e.g. +1 for US).
          </div>
        )}
      </div>
      <button onClick={onNext} style={{
        marginTop:24, width:"100%", padding:"16px",
        background: COLOR.gold,
        color: COLOR.night, border:"none", borderRadius:14,
        fontFamily:"'DM Mono', monospace", fontSize:13, fontWeight:600,
        letterSpacing:"0.08em", cursor: "pointer", transition:"all 0.3s"
      }}>{canSkip ? "SKIP FOR NOW →" : "CONTINUE →"}</button>
    </div>
  );
}

// UsernameScreen — public handle picker. Required, not skippable —
// every new account claims a handle so the directory is searchable
// the moment they hit Home. Debounced availability check; CONTINUE
// only enables once the input is valid AND free.
function UsernameScreen({ value, onChange, onNext, onBack, step, total, defaultSeed, currentUsername = "" }) {
  const trimmed = (value || "").trim().toLowerCase();
  const isValid = /^[a-z0-9_]{3,24}$/.test(trimmed);
  const [checkState, setCheckState] = useState("idle"); // idle | checking | available | taken | invalid

  // Default-seed once on mount if the field is empty (derive from
  // user's first name / email handle if we have one). User can edit.
  useEffect(() => {
    if (!value && defaultSeed) {
      const seed = String(defaultSeed)
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, "")
        .slice(0, 24);
      if (seed.length >= 3) onChange(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced availability check.
  useEffect(() => {
    if (!trimmed) { setCheckState("idle"); return; }
    if (!isValid) { setCheckState("invalid"); return; }
    // Re-onboarding a row that already owns this handle: the
    // username_available RPC doesn't exclude the caller's own row,
    // so it would report the user's OWN handle as taken and hard-
    // block the step.
    if (currentUsername && trimmed === currentUsername.toLowerCase()) {
      setCheckState("available");
      return;
    }
    setCheckState("checking");
    const t = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc("username_available", { p_handle: trimmed });
        if (error) throw error;
        setCheckState(data ? "available" : "taken");
      } catch (e) {
        console.warn("[onboarding] username_available failed:", e?.message || e);
        setCheckState("idle");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [trimmed, isValid, currentUsername]);

  // REQUIRED: must be a valid + available handle. No skip path.
  const canContinue = checkState === "available";
  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", padding:"24px 20px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:COLOR.sub, fontSize:20, cursor:"pointer" }}>←</button>
        <ProgressDots total={total} current={step} />
        <div style={{ width:28 }} />
      </div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:10 }}>
          STEP {step + 1} OF {total}
        </div>
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:34, fontWeight:300, fontStyle:"italic",
          color:COLOR.ink, letterSpacing:"-0.02em", lineHeight:1.1 }}>Pick your handle</h2>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.ink, marginTop:14, lineHeight:1.55 }}>
          This is how friends find and tag you on Booda — "I'm @<strong>{trimmed || "you"}</strong>." It shows under your name on your profile and on every cook log you post.
        </p>
      </div>
      <div style={{ flex:1 }}>
        <div style={{
          display:"flex", alignItems:"center", gap:0,
          padding:"0 18px", borderRadius:14,
          border:`1.5px solid ${checkState === "available" ? COLOR.gold : checkState === "taken" || checkState === "invalid" ? COLOR.rose : COLOR.border}`,
          background:COLOR.soil,
        }}>
          <span style={{ fontFamily:"'DM Mono', monospace", fontSize:18, color:COLOR.muted }}>@</span>
          <input
            type="text"
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={value}
            onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24))}
            placeholder="yourname"
            style={{
              flex:1, padding:"16px 6px",
              border:"none", background:"transparent",
              color:COLOR.ink,
              fontFamily:"'DM Mono', monospace", fontSize:18,
              outline:"none", letterSpacing:"0.02em",
            }}
          />
        </div>
        <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, marginTop:10, textAlign:"center", lineHeight:1.5 }}>
          {checkState === "checking"  && <span style={{ color:COLOR.muted }}>Checking…</span>}
          {checkState === "available" && <span style={{ color:COLOR.leaf }}>@{trimmed} is available ✓</span>}
          {checkState === "taken"     && <span style={{ color:COLOR.rose }}>@{trimmed} is taken — try another</span>}
          {checkState === "invalid"   && <span style={{ color:COLOR.rose }}>3–24 chars, lowercase letters / numbers / underscore</span>}
          {checkState === "idle"      && <span style={{ color:COLOR.muted }}>Lowercase letters, numbers, and underscore.</span>}
        </div>
      </div>
      <button
        onClick={canContinue ? onNext : undefined}
        disabled={!canContinue}
        style={{
          marginTop:24, width:"100%", padding:"16px",
          background: canContinue ? COLOR.gold : COLOR.soil,
          color: canContinue ? COLOR.night : COLOR.sub,
          border:"none", borderRadius:14,
          fontFamily:"'DM Mono', monospace", fontSize:13, fontWeight:600,
          letterSpacing:"0.08em", cursor: canContinue ? "pointer" : "not-allowed",
          transition:"all 0.3s",
        }}
      >
        {checkState === "available" ? "CONTINUE →" : "PICK AN AVAILABLE HANDLE"}
      </button>
    </div>
  );
}

// ── Shared chrome for the newer screens ─────────────────────────────
// Header + kicker + title + flexible body + footer. The original
// screens carry their own copies of this boilerplate; new screens use
// the shell so persona-driven step counts stay consistent.
function StepShell({ onBack, step, total, kicker, title, subtitle, children, footer }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", padding:"24px 20px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:COLOR.sub, fontSize:20, cursor:"pointer", padding:"6px 12px", margin:"-6px -12px" }}>←</button>
        <ProgressDots total={total} current={step} />
        <div style={{ width:28 }} />
      </div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:10 }}>
          {kicker || `STEP ${step + 1} OF ${total}`}
        </div>
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:34, fontWeight:300, fontStyle:"italic",
          color:COLOR.ink, letterSpacing:"-0.02em", lineHeight:1.1 }}>{title}</h2>
        {subtitle && <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.muted, marginTop:8, lineHeight:1.5 }}>{subtitle}</p>}
      </div>
      <div style={{ flex:1 }}>{children}</div>
      {footer}
    </div>
  );
}

function GoldButton({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      marginTop:24, width:"100%", padding:"16px",
      background: disabled ? COLOR.soil : COLOR.gold,
      color: disabled ? COLOR.sub : COLOR.night, border:"none", borderRadius:14,
      fontFamily:"'DM Mono', monospace", fontSize:13, fontWeight:600,
      letterSpacing:"0.08em", cursor: disabled ? "not-allowed" : "pointer", transition:"all 0.3s"
    }}>{children}</button>
  );
}

// ── Name + photo ────────────────────────────────────────────────────
// The face of the account: display name (prefilled from the OAuth
// provider when available) and a profile picture — either an uploaded
// photo (compressed, self-foldered in the profile-photos bucket) or
// one of the starter avatars the grant RPC seeded.
function NamePhotoScreen({ answers, set, avatars, userId, onNext, onBack, step, total }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const catalog = Array.isArray(avatars?.catalog) ? avatars.catalog : [];
  const ownedSet = avatars?.owned instanceof Set ? avatars.owned : new Set();
  const ownedAvatars = catalog.filter((c) => ownedSet.has(c.slug));
  // Starter grant may not have landed yet on a brand-new account —
  // show the front of the catalog so the row is never empty.
  const tiles = (ownedAvatars.length > 0 ? ownedAvatars : catalog).slice(0, 8);

  const pickPhoto = async (file) => {
    if (!file || busy || !userId) return;
    setBusy(true);
    setErr("");
    try {
      const { compressImage } = await import("../lib/compressImage");
      const blob = await compressImage(file, { maxDimension: 512, jpegQuality: 0.8 });
      const path = `${userId}/${Date.now()}.jpg`;
      const { error } = await supabase.storage
        .from("profile-photos")
        .upload(path, blob, { contentType: "image/jpeg", upsert: true, cacheControl: "3600" });
      if (error) throw error;
      const { data } = supabase.storage.from("profile-photos").getPublicUrl(path);
      set("photoUrl", data.publicUrl);
      set("avatarSlug", null);
      set("avatarUrl", null);
    } catch (e) {
      setErr(e?.message || "Upload didn't take — try another photo.");
    } finally {
      setBusy(false);
    }
  };

  const tileStyle = (selected) => ({
    width: 64, height: 64, borderRadius: "50%", flexShrink: 0, padding: 0,
    border: `2.5px solid ${selected ? COLOR.gold : COLOR.border}`,
    background: COLOR.soil, cursor: "pointer", overflow: "hidden",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: selected ? "0 0 0 1px #f5c84244, 0 4px 18px #f5c84222" : "none",
  });

  return (
    <StepShell
      onBack={onBack} step={step} total={total}
      title="Who's cooking?"
      subtitle="Your name and face — this is what family and friends see on your cooks."
    >
      <input
        type="text"
        value={answers.name}
        onChange={(e) => set("name", e.target.value)}
        placeholder="Your name"
        autoComplete="name"
        autoFocus={!answers.name}
        style={{
          width:"100%", boxSizing:"border-box", padding:"16px 18px", borderRadius:14,
          border:`1.5px solid ${answers.name.trim() ? COLOR.gold : COLOR.border}`,
          background:COLOR.soil, color:COLOR.ink,
          fontFamily:"'DM Sans', sans-serif", fontSize:17,
          outline:"none", textAlign:"center",
        }}
      />
      <div style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.sub, letterSpacing:"0.12em", margin:"22px 0 10px" }}>
        PICK A FACE
      </div>
      <div style={{ display:"flex", gap:10, overflowX:"auto", padding:"4px 2px 8px", scrollbarWidth:"none", WebkitOverflowScrolling:"touch" }}>
        <input
          ref={fileRef}
          type="file" accept="image/*"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickPhoto(f); }}
          style={{ display:"none" }}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          style={answers.photoUrl
            ? tileStyle(true)
            : { ...tileStyle(false), border:`2px dashed ${COLOR.goldDim || COLOR.gold}` }}
          aria-label="Upload a photo"
        >
          {answers.photoUrl
            ? <img src={answers.photoUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
            : <span style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.gold, lineHeight:1.2 }}>{busy ? "…" : "+ PHOTO"}</span>}
        </button>
        {tiles.map((c) => {
          const selected = !answers.photoUrl && answers.avatarSlug === c.slug;
          return (
            <button
              key={c.slug}
              // avatarUrl rides along because every render site in the
              // app keys on profiles.avatar_url — writing the slug
              // alone would leave the boot-grant's random starter
              // showing instead of the picked one.
              onClick={() => { set("avatarSlug", c.slug); set("avatarUrl", c.image_url || null); set("photoUrl", null); }}
              style={tileStyle(selected)}
              aria-label={c.name || c.slug}
            >
              <img src={c.image_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
            </button>
          );
        })}
      </div>
      {err && (
        <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.rose, marginTop:8 }}>
          {err}
        </div>
      )}
      <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.muted, marginTop:10, lineHeight:1.5 }}>
        Photo optional — pick a character instead and change it anytime.
      </p>
      <GoldButton onClick={onNext} disabled={!answers.name.trim() || busy}>
        CONTINUE →
      </GoldButton>
    </StepShell>
  );
}

// ── Cooking style persona ───────────────────────────────────────────
// Skippable, but load-bearing when picked: it sets the STEP SET for
// the rest of onboarding, the tour chapters, the Home checklist, and
// whether the kitchen/pantry feature pushes itself (kitchen_tracking
// preset). A mood, not a tattoo — editable in Settings later.
const COOK_STYLES = [
  { id: "simple",   label: "Keep it simple",        desc: "A few reliable meals, minimum fuss. Chicken and rice is a lifestyle." },
  { id: "dayof",    label: "I shop the day I cook", desc: "No standing inventory — grab tonight's ingredients, cook them tonight." },
  { id: "stocker",  label: "I keep a stocked kitchen", desc: "Track what's on hand, use things up, waste less." },
  { id: "explorer", label: "Feed me everything",    desc: "New recipes, new cuisines, all the time. The book can never be too big." },
];

function PersonaScreen({ value, onSelect, onNext, onSkip, onBack, step, total }) {
  return (
    <StepShell
      onBack={onBack} step={step} total={total}
      title="How do you cook?"
      subtitle="This tunes the whole app around you — change it anytime in Settings."
      footer={
        <>
          <GoldButton onClick={onNext} disabled={!value}>CONTINUE →</GoldButton>
          <button onClick={onSkip} style={{
            marginTop:12, width:"100%", background:"none", border:"none",
            fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.muted,
            letterSpacing:"0.08em", cursor:"pointer", padding:"8px 4px",
          }}>SKIP — I'LL DECIDE LATER</button>
        </>
      }
    >
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {COOK_STYLES.map((s) => {
          const selected = value === s.id;
          return (
            <button key={s.id} onClick={() => onSelect(s.id)} style={{
              width:"100%", textAlign:"left",
              border:`1.5px solid ${selected ? COLOR.gold : COLOR.border}`,
              borderRadius:14, padding:"16px",
              background: selected ? COLOR.goldDeep : COLOR.soil,
              cursor:"pointer",
              boxShadow: selected ? "0 0 0 1px #f5c84244, 0 4px 24px #f5c84211" : "none",
              transition:"all 0.2s",
            }}>
              <div style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:16,
                color: selected ? COLOR.gold : COLOR.ink, fontWeight:500 }}>
                {s.label}
              </div>
              <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.muted, marginTop:3, lineHeight:1.45 }}>
                {s.desc}
              </div>
            </button>
          );
        })}
      </div>
    </StepShell>
  );
}

// ── Allergies ───────────────────────────────────────────────────────
// Safety-critical: written once at completion (controlled mode — no
// mid-onboarding network writes). onScanPhoto is the allergy-sheet
// photo → AI-extraction entry (wired by the parse-allergy-sheet flow).
function AllergiesScreen({ value, onChange, onNext, onBack, step, total }) {
  const count = Array.isArray(value) ? value.length : 0;
  // Allergy-sheet photo import (parse-allergy-sheet fn + mandatory
  // review). Confirmed chips land in the STEP's local state — the
  // profile write happens once at onboarding completion.
  const [sheetOpen, setSheetOpen] = useState(false);
  return (
    <StepShell
      onBack={onBack} step={step} total={total}
      title="Any allergies?"
      subtitle="These are hard exclusions — flagged on recipes and never generated into your meals. Separate from diet preferences."
      footer={
        <GoldButton onClick={onNext} disabled={false}>
          {count > 0 ? "CONTINUE →" : "SKIP — NO ALLERGIES →"}
        </GoldButton>
      }
    >
      <AllergiesSection value={value} onChange={onChange} onScanPhoto={() => setSheetOpen(true)} hideHeader />
      <AllergySheetReview
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        existing={value}
        onSave={(merged) => onChange(merged)}
      />
    </StepShell>
  );
}

// ── Optional password ───────────────────────────────────────────────
// The user is already authenticated mid-onboarding, so setting a
// password is one updateUser call — no email round trip. Fully
// skippable; magic codes and Google keep working regardless.
function PasswordScreen({ onNext, onBack, step, total }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const valid = pw.length >= 8 && pw === pw2;
  const mismatch = pw2.length > 0 && pw !== pw2;

  const saveAndNext = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setErr("");
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) {
      setErr(error.message || "Couldn't save the password — you can set one later in Settings.");
      return;
    }
    onNext();
  };

  const inputStyle = (bad) => ({
    width:"100%", boxSizing:"border-box", padding:"15px 18px", borderRadius:14,
    border:`1.5px solid ${bad ? COLOR.rose : COLOR.border}`,
    background:COLOR.soil, color:COLOR.ink,
    fontFamily:"'DM Sans', sans-serif", fontSize:16,
    outline:"none", textAlign:"center",
  });

  return (
    <StepShell
      onBack={onBack} step={step} total={total}
      title="Add a password?"
      subtitle="Sign in without waiting for an email. You can always use a magic code or Google too."
      footer={
        <>
          <GoldButton onClick={pw ? saveAndNext : onNext} disabled={busy || (pw.length > 0 && !valid)}>
            {busy ? "SAVING…" : pw ? "SAVE & CONTINUE →" : "SKIP FOR NOW →"}
          </GoldButton>
          {pw.length > 0 && (
            <button onClick={onNext} style={{
              marginTop:12, width:"100%", background:"none", border:"none",
              fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.muted,
              letterSpacing:"0.08em", cursor:"pointer", padding:"8px 4px",
            }}>SKIP FOR NOW</button>
          )}
        </>
      }
    >
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        <input type="password" autoComplete="new-password" value={pw}
          onChange={(e) => { setPw(e.target.value); setErr(""); }}
          placeholder="Password (8+ characters)" style={inputStyle(false)} />
        <input type="password" autoComplete="new-password" value={pw2}
          onChange={(e) => { setPw2(e.target.value); setErr(""); }}
          placeholder="Repeat it" style={inputStyle(mismatch)} />
        {mismatch && (
          <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:12, color:COLOR.rose, textAlign:"center" }}>
            Those don't match yet.
          </div>
        )}
        {err && (
          <div style={{ fontFamily:"'DM Sans', sans-serif", fontSize:13, color:COLOR.rose, textAlign:"center" }}>
            {err}
          </div>
        )}
      </div>
    </StepShell>
  );
}

// ── Step sets per persona ───────────────────────────────────────────
// One ordered array filtered per cooking style — NOT scattered
// conditionals. The persona sits at index 1, so picking one reshapes
// everything after it while earlier answers persist.
const FULL_STEPS = ["namePhoto","persona","username","dietary","veganstyle","allergies","level","goal","cities","phone","password"];
const STEPS_BY_STYLE = {
  simple: ["namePhoto","persona","username","allergies","password"],
  dayof:  ["namePhoto","persona","username","dietary","veganstyle","allergies","cities","password"],
};

function stepsFor(cookStyle, dietary) {
  const base = STEPS_BY_STYLE[cookStyle] || FULL_STEPS;
  return base.filter((id) => id !== "veganstyle" || dietary === "vegan");
}

export default function Onboarding({ onComplete, defaultName = "", existingProfile = null, avatars = null, userId = null }) {
  const [phase, setPhase] = useState("splash"); // splash | steps | building | error
  const [idx, setIdx] = useState(0);
  const [finishError, setFinishError] = useState("");
  // Prefill from any existing profile values — husk rows created by
  // the old gate bug may carry Settings-set fields; re-onboarding
  // must show them, never blank them.
  const [answers, setAnswers] = useState(() => ({
    name:       existingProfile?.name || defaultName || "",
    photoUrl:   null,
    avatarSlug: null,
    avatarUrl:  null,
    cookStyle:  existingProfile?.cook_style || null,
    username:   existingProfile?.username || "",
    dietary:    existingProfile?.dietary || "",
    veganStyle: existingProfile?.vegan_style || "",
    allergies:  Array.isArray(existingProfile?.allergies) ? existingProfile.allergies : [],
    level:      existingProfile?.skill_self_report || "",
    goal:       existingProfile?.goal || "",
    shoppingCities: Array.isArray(existingProfile?.shopping_cities) ? existingProfile.shopping_cities : [],
    phone:      "",
  }));
  const set = (key, val) => setAnswers(a => ({ ...a, [key]: val }));

  const steps = stepsFor(answers.cookStyle, answers.dietary);
  const TOTAL = steps.length;
  const stepId = steps[Math.min(idx, TOTAL - 1)];
  const next = () => {
    if (idx + 1 >= TOTAL) setPhase("building");
    else setIdx(idx + 1);
  };
  const back = () => {
    if (idx === 0) setPhase("splash");
    else setIdx(idx - 1);
  };

  const finish = async () => {
    // Only submit what the persona's step set actually SHOWED — the
    // completion upsert writes collected keys only, so hidden steps
    // can't blank values a husk row already had.
    const visible = new Set(steps);
    try {
      await onComplete({
        name:       answers.name,
        photoUrl:   answers.photoUrl,
        avatarSlug: answers.avatarSlug,
        avatarUrl:  answers.avatarUrl,
        cookStyle:  answers.cookStyle,
        username:   answers.username,
        allergies:  visible.has("allergies") ? answers.allergies : undefined,
        dietary:    visible.has("dietary") ? answers.dietary : undefined,
        veganStyle: visible.has("dietary") ? answers.veganStyle : undefined,
        level:      visible.has("level") ? answers.level : undefined,
        goal:       visible.has("goal") ? answers.goal : undefined,
        shoppingCities: visible.has("cities") ? answers.shoppingCities : undefined,
        phone:      visible.has("phone") ? answers.phone : "",
      });
    } catch (e) {
      // Without this, a failed completion upsert (network flake,
      // username unique-violation) strands the user on the building
      // screen at 100% forever, and every collected answer dies with
      // an app kill. Answers are still in state — offer a retry.
      console.warn("[onboarding] completion failed:", e?.message || e);
      setFinishError(e?.message || "Something went sideways saving your profile.");
      setPhase("error");
    }
  };

  if (phase === "splash")   return <SplashScreen onNext={() => { setIdx(0); setPhase("steps"); }} />;
  if (phase === "building") return <BuildingScreen profile={answers} onDone={finish} />;
  if (phase === "error") {
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", padding:40, textAlign:"center" }}>
        <img src="/boodalogo.png" alt="" aria-hidden style={{ width:120, height:"auto", marginBottom:24, opacity:0.8 }} />
        <h2 style={{ fontFamily:"'forma-djr-deck', system-ui, sans-serif", fontSize:26, fontWeight:300, fontStyle:"italic", color:COLOR.ink, marginBottom:10 }}>
          That didn't save
        </h2>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, color:COLOR.muted, lineHeight:1.55, maxWidth:300, marginBottom:8 }}>
          Your answers are safe on this screen — nothing was lost.
        </p>
        <p style={{ fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.rose, maxWidth:320, marginBottom:28, wordBreak:"break-word" }}>
          {finishError}
        </p>
        <button onClick={() => { setPhase("building"); }} style={{
          background:COLOR.gold, color:COLOR.night, border:"none", borderRadius:50,
          padding:"16px 40px", fontFamily:"'DM Mono', monospace", fontSize:13,
          fontWeight:600, letterSpacing:"0.08em", cursor:"pointer",
        }}>TRY AGAIN</button>
        <button onClick={() => { setIdx(TOTAL - 1); setPhase("steps"); }} style={{
          marginTop:14, background:"none", border:"none",
          fontFamily:"'DM Mono', monospace", fontSize:12, color:COLOR.muted,
          letterSpacing:"0.08em", cursor:"pointer", padding:"8px 4px",
        }}>BACK TO THE LAST STEP</button>
      </div>
    );
  }

  const common = { step: idx, total: TOTAL, onNext: next, onBack: back };
  switch (stepId) {
    case "namePhoto":
      return <NamePhotoScreen answers={answers} set={set} avatars={avatars} userId={userId} {...common} />;
    case "persona":
      return <PersonaScreen value={answers.cookStyle} onSelect={v => set("cookStyle", v)}
        onSkip={() => { set("cookStyle", null); next(); }} {...common} />;
    case "username":
      return <UsernameScreen value={answers.username} onChange={v => set("username", v)}
        currentUsername={existingProfile?.username || ""}
        defaultSeed={(answers.name || "").split(/\s+/)[0] || ""} {...common} />;
    case "dietary":
      return <QuestionScreen title="How do you eat?" subtitle="Pick the one that fits best. You can always change it later."
        options={DIETARY_OPTIONS} value={answers.dietary} onSelect={v => set("dietary", v)} wide {...common} />;
    case "veganstyle":
      return <VeganStyleScreen value={answers.veganStyle} onSelect={v => set("veganStyle", v)} {...common} />;
    case "allergies":
      return <AllergiesScreen value={answers.allergies} onChange={v => set("allergies", v)}
        onScanPhoto={null} {...common} />;
    case "level":
      return <QuestionScreen title="How comfortable are you in the kitchen?" subtitle="Be honest — we won't judge."
        options={LEVEL_OPTIONS} value={answers.level} onSelect={v => set("level", v)} {...common} />;
    case "goal":
      return <QuestionScreen title="What do you want to get out of this?" subtitle="This shapes your entire skill tree."
        options={GOAL_OPTIONS} value={answers.goal} onSelect={v => set("goal", v)} {...common} />;
    case "cities":
      return <CityScreen value={answers.shoppingCities} onChange={v => set("shoppingCities", v)} {...common} />;
    case "phone":
      return <PhoneScreen value={answers.phone} onChange={v => set("phone", v)} {...common} />;
    case "password":
      return <PasswordScreen {...common} />;
    default:
      return null;
  }
}
